import React, { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, ChevronDown, PenLine, ArrowRight, Sparkles, Layers, LayoutTemplate, Briefcase, Wallet } from "lucide-react";
import { api } from "../lib/api";

const STEPS = ["Uploading file", "Extracting text", "Analyzing with AI", "Building dashboard"];

const CURRENCIES = [
  { code: "INR", label: "₹ lakh" }, { code: "USD", label: "$" }, { code: "GBP", label: "£" },
  { code: "EUR", label: "€" }, { code: "AED", label: "AED" }, { code: "SGD", label: "S$" },
  { code: "AUD", label: "A$" }, { code: "CAD", label: "C$" },
];

/** Every optional block looks and behaves the same way: one collapsible card. */
const Section = ({ id, icon: Icon, tone, title, optional = true, open, onToggle, children }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50" data-testid={`${id}-section`}>
    <button onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left" data-testid={`${id}-toggle`}>
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Icon className={`h-4 w-4 ${tone}`} /> {title} {optional && <span className="font-normal text-slate-500">(Optional)</span>}
      </span>
      <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="px-4 pb-4">{children}</div>}
  </div>
);

const ResumeIntake = ({ onAnalyze, onCancel, loading, step, progress, onBuildFromScratch, onTemplateOnly, savedResumes = [] }) => {
  const [file, setFile] = useState(null);
  const [jd, setJd] = useState("");
  const [showJd, setShowJd] = useState(false);
  const [company, setCompany] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [dragging, setDragging] = useState(false);
  const [ownSummary, setOwnSummary] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [preference, setPreference] = useState("");
  const [preferenceNote, setPreferenceNote] = useState("");
  const [showPreference, setShowPreference] = useState(false);
  const [showSalary, setShowSalary] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [currentCtc, setCurrentCtc] = useState("");
  const [expectedCtc, setExpectedCtc] = useState("");
  const [salaryCurrency, setSalaryCurrency] = useState("INR");
  const [ladders, setLadders] = useState([]);
  const [templateTarget, setTemplateTarget] = useState("");
  const inputRef = useRef();

  React.useEffect(() => {
    api.get("/future-jobs/meta").then((r) => setLadders(r.data.ladders || [])).catch(() => {});
  }, []);

  const ladderFor = ladders.find((l) => l.steps.some((s) => s.title === preference));

  const pick = (f) => {
    if (!f || loading) return;
    if (!/\.(pdf|docx)$/i.test(f.name) || f.size > 10 * 1024 * 1024 || !f.size) {
      toast.error("Choose a non-empty PDF or DOCX up to 10MB.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(f);
  };

  const remove = (event) => {
    event.stopPropagation();
    onCancel?.();
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = () => {
    if (file && !loading) onAnalyze(file, { jd, company, roleTitle, ownSummary, preference, preferenceNote, currentCtc, expectedCtc, salaryCurrency });
  };

  const unitHint = salaryCurrency === "INR" ? "e.g. 28 (lakh)" : "e.g. 120000";

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 md:p-8 shadow-xl" data-testid="resume-intake-card">
      <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Start with your resume</h2>
      <p className="text-sm text-slate-500 mt-1.5">Already have one? Upload it. Don't have one yet? We'll build it with you, question by question.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="intake-mode-cards">
        <div className="accent-bar rounded-xl border-2 border-sky-300 bg-sky-50/60 p-4 pl-5" style={{ "--accent": "#0284C7" }} data-testid="intake-mode-upload">
          <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><UploadCloud className="w-4 h-4 text-sky-600" /> I have a resume</p>
          <p className="mt-1.5 text-xs text-slate-600">Upload a PDF or DOCX below and we'll analyse it straight away.</p>
        </div>
        <button onClick={onBuildFromScratch} disabled={loading} className="accent-bar card-soft-hover rounded-xl border border-slate-200 bg-white p-4 pl-5 text-left disabled:opacity-50" style={{ "--accent": "#0D9488" }} data-testid="intake-mode-builder">
          <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><PenLine className="w-4 h-4 text-teal-600" /> Build it from scratch</p>
          <p className="mt-1.5 text-xs text-slate-600">No resume yet? Answer guided questions, pick a template, and we'll write it for you.</p>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">Start building <ArrowRight className="w-3.5 h-3.5" /></span>
        </button>
      </div>

      <p className="mt-5 text-[11px] font-mono uppercase tracking-wider text-slate-500">Upload your resume</p>
      <p className="text-sm text-slate-500 mt-1">PDF or DOCX. Job description is optional — the dashboard works without it.</p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files[0]); }}
        onClick={() => !loading && inputRef.current?.click()}
        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ") && !loading) { e.preventDefault(); inputRef.current?.click(); } }}
        role="button" tabIndex={0} aria-label="Choose a resume"
        className={`mt-5 cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-sky-200 bg-sky-50" : "border-slate-300 hover:border-slate-300"}`}
        data-testid="resume-dropzone"
      >
        <input ref={inputRef} type="file" accept=".pdf,.docx" disabled={loading} className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="resume-upload-input" />
        {file ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-slate-800"><FileText className="w-5 h-5 shrink-0 text-sky-600" /><span className="font-medium break-all" data-testid="selected-resume-filename">{file.name}</span></div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" onClick={remove} className="h-10 border-slate-300" data-testid="remove-resume-button">{loading ? "Cancel upload / analysis" : "Cancel / Remove"}</Button>
              <Button variant="outline" disabled={loading} onClick={(e) => { e.stopPropagation(); inputRef.current.value = ""; inputRef.current.click(); }} className="h-10 border-slate-300" data-testid="reupload-resume-button">Re-upload</Button>
            </div>
          </div>
        ) : (
          <>
            <UploadCloud className="w-9 h-9 text-slate-500 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Drop your resume here or click to browse</p>
            <p className="text-xs text-slate-500 mt-1">Max 10MB · PDF / DOCX · security-scanned on upload</p>
          </>
        )}
      </div>

      <p className="mt-6 rounded-xl border border-sky-200 bg-sky-50/60 px-4 py-3 text-xs text-slate-700" data-testid="optional-details-note">
        Everything below is optional — but the more you tell us, the sharper your results get. Salary, target role and your own
        words are what turn a generic analysis into a job search that actually fits you.
      </p>

      <div className="mt-3 space-y-3">
        <Section id="jd" icon={Briefcase} tone="text-sky-600" title="Job Description of the job you want to apply for" open={showJd} onToggle={() => setShowJd((v) => !v)}>
          <p className="text-xs text-slate-600">Paste the posting you are aiming at. We score Role Alignment against its exact words.</p>
          <Textarea
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            placeholder="Paste a job description to score Role Alignment against it…"
            className="mt-3 min-h-[120px] bg-white border-slate-200 text-slate-800 placeholder:text-slate-500"
            data-testid="jd-textarea" maxLength={20000} disabled={loading}
          />
          <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="optional-job-fields">
            <label className="text-sm text-slate-700">Company Name <span className="text-slate-500">(Optional)</span><Input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={200} disabled={loading} placeholder="e.g. - Google" className="mt-2 bg-white border-slate-200" data-testid="target-company-input" /></label>
            <label className="text-sm text-slate-700">Name Of The Role <span className="text-slate-500">(Optional)</span><Input value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} maxLength={200} disabled={loading} placeholder="e.g. - Director, Site Reliability Engineering" className="mt-2 bg-white border-slate-200" data-testid="target-role-input" /></label>
          </div>
        </Section>

        <Section id="salary" icon={Wallet} tone="text-emerald-600" title="Your salary, current and expected" open={showSalary} onToggle={() => setShowSalary((v) => !v)}>
          <p className="text-xs text-slate-600">These two numbers drive every salary band we show you — matched roles, job suggestions and where you sit on the career pyramid. Nothing here goes on your resume.</p>
          <div className="mt-3 grid items-end gap-3 sm:grid-cols-[160px_1fr_1fr]">
            <label className="flex h-full flex-col justify-end text-[11px] uppercase tracking-wider text-slate-500">
              <span>Currency</span>
              <select value={salaryCurrency} onChange={(e) => setSalaryCurrency(e.target.value)} disabled={loading}
                className="mt-1 block h-10 w-full rounded-md border border-slate-200 bg-white px-3 font-sans text-sm text-slate-900" data-testid="salary-currency-select">
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} · {c.label}</option>)}
              </select>
            </label>
            <label className="flex h-full flex-col justify-end text-[11px] uppercase tracking-wider text-slate-500">
              <span>Current Salary (CTC) Per Annum</span>
              <Input value={currentCtc} onChange={(e) => setCurrentCtc(e.target.value)} maxLength={40} disabled={loading}
                placeholder={unitHint} className="mt-1 h-10 bg-white border-slate-200 font-sans text-sm" data-testid="current-ctc-input" />
            </label>
            <label className="flex h-full flex-col justify-end text-[11px] uppercase tracking-wider text-slate-500">
              <span>Expected Salary (CTC) Per Annum</span>
              <Input value={expectedCtc} onChange={(e) => setExpectedCtc(e.target.value)} maxLength={40} disabled={loading}
                placeholder={unitHint} className="mt-1 h-10 bg-white border-slate-200 font-sans text-sm" data-testid="expected-ctc-input" />
            </label>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">{salaryCurrency === "INR"
            ? "Enter in lakh per annum — 28 means ₹28 L. Full figures like 2800000 work too."
            : `Enter the annual figure in ${salaryCurrency}. Our pay bands and level checks are published in Indian lakh, so for now we use a non-INR figure as context rather than as a filter.`}</p>
        </Section>

        <Section id="own-summary" icon={Sparkles} tone="text-sky-600" title="Summary of your experience in your words" open={showSummary} onToggle={() => setShowSummary((v) => !v)}>
          <p className="text-xs text-slate-600">Tell us the work you are genuinely proud of — even the parts you left off your resume because you weren't sure they mattered. We read this as real evidence when we match you.</p>
          <Textarea value={ownSummary} onChange={(e) => setOwnSummary(e.target.value)} maxLength={6000} disabled={loading}
            placeholder={"In your words, what are you actually good at?\n\nFor example:\n• The problem you fixed that nobody else could, and what it saved\n• A team, migration or audit you quietly carried\n• Tools or domains you know deeply but never listed\n• Work outside your job description: mentoring, pre-sales, firefighting, automation\n• What you want to be known for next"}
            className="mt-3 min-h-[150px] bg-white border-slate-200 text-slate-800 placeholder:text-slate-400" data-testid="own-summary-textarea" />
          <p className="mt-1 text-right text-[11px] text-slate-400">{ownSummary.length} / 6000</p>
        </Section>

        <Section id="job-preference" icon={Layers} tone="text-teal-600" title="Your job preference" open={showPreference} onToggle={() => setShowPreference((v) => !v)}>
          <p className="text-xs text-slate-600">The role you dream of next — even if you have no idea how to get there. We'll use it to rank your Future Jobs fit and show you the levels in between.</p>
          <label className="mt-3 block text-[11px] uppercase tracking-wider text-slate-500">Dream / next-level role
            <select value={preference} onChange={(e) => setPreference(e.target.value)} disabled={loading}
              className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900" data-testid="job-preference-select">
              <option value="">Not sure yet</option>
              {ladders.map((ladder) => (
                <optgroup key={ladder.key} label={ladder.label}>
                  {ladder.steps.map((stepItem) => <option key={stepItem.title} value={stepItem.title}>{stepItem.title}</option>)}
                </optgroup>
              ))}
              <option value="Other">Other (tell us below)</option>
            </select>
          </label>
          {ladderFor && (
            <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50/60 p-3" data-testid="job-preference-ladder">
              <p className="text-[10px] font-mono uppercase tracking-wider text-teal-700">{ladderFor.label} · what separates the levels</p>
              <ol className="mt-2 space-y-1.5">
                {ladderFor.steps.map((stepItem, i) => (
                  <li key={stepItem.title} className={`text-xs ${stepItem.title === preference ? "text-slate-900 font-semibold" : "text-slate-600"}`}>
                    <span className="num-display mr-1.5 text-slate-400">{i + 1}</span>{stepItem.title} — <span className="font-normal text-slate-600">{stepItem.separator}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <label className="mt-3 block text-[11px] uppercase tracking-wider text-slate-500">Anything else about where you want to go
            <Textarea value={preferenceNote} onChange={(e) => setPreferenceNote(e.target.value)} maxLength={2000} disabled={loading}
              placeholder="e.g. I have been an SME for 9 years and want to move into architecture, but I don't know which kind of architect fits me."
              className="mt-1 min-h-[80px] bg-white border-slate-200 text-slate-800 placeholder:text-slate-400" data-testid="job-preference-note" />
          </label>
        </Section>

        {savedResumes.length > 0 && (
          <Section id="template-only" icon={LayoutTemplate} tone="text-violet-600" title="Just upgrade my resume template" optional={false}
            open={showTemplate} onToggle={() => setShowTemplate((v) => !v)}>
            <p className="text-xs text-slate-600">Keep every word exactly as it is — change only the template and layout, then download. Free on every plan; premium templates stay on paid plans.</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <label className="block text-[11px] uppercase tracking-wider text-slate-500">Which resume
                <select value={templateTarget || savedResumes[0].resume_id} onChange={(e) => setTemplateTarget(e.target.value)} disabled={loading}
                  className="mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3 font-sans text-sm text-slate-900" data-testid="template-only-resume-select">
                  {savedResumes.map((r) => (
                    <option key={r.resume_id} value={r.resume_id}>
                      {`${r.filename.length > 38 ? `${r.filename.slice(0, 38)}…` : r.filename} · ${new Date(r.created_at).toLocaleDateString()}`}
                    </option>
                  ))}
                </select>
              </label>
              <Button onClick={() => onTemplateOnly?.(templateTarget || savedResumes[0].resume_id)} disabled={loading}
                className="h-11 rounded-xl bg-violet-600 px-4 text-white hover:bg-violet-700" data-testid="template-only-button">
                Pick a new template <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </div>
          </Section>
        )}
      </div>

      <Button
        onClick={submit}
        disabled={!file || loading}
        className="mt-6 w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-semibold text-base disabled:opacity-40"
        data-testid="analyze-resume-button"
      >
        {loading ? (
          <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {step === 0 ? `Uploading ${progress || 0}%` : STEPS[step] || "Working"}…</span>
        ) : "Parse & Analyze"}
      </Button>

      {loading && (
        <div className="mt-4 flex items-center gap-2" data-testid="analyze-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-sky-500" : "bg-slate-100"}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResumeIntake;
